//Node Modules
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

//BinaryForge Components
import { PillItem } from "../helpers";

//Atoms
import { deviceSensorAlarmListAtom } from "../../atoms/alarm";

//Helpers
import { useDeviceOfflineState } from "../../helpers/useDeviceOfflineState";
import { getUiDateTime } from "../../helpers/DateUtils";

//Online (green) / Offline (grey) pill for a device.
//
//Offline comes from the backend's open connection-lost event, so this pill says the same thing
//the customer's notification said.
//
//heuristicOnly is for the cross-scheme admin table. LiveEvent is indexed per scheme, so that
//table has no event data to read and falls back to the reporting-interval estimate, saying so in
//its tooltip.
//
//showWarning is opt-in, because a warning on six screens at once is noise rather than a signal.
//Only the devices table and the device detail page ask for it.
export const ConnectivityPill = ({ device, heuristicOnly = false, showWarning = false }) => {
	const { t } = useTranslation();
	const getOfflineState = useDeviceOfflineState();
	const { presence, isOffline, offlineSince, isSilentWithoutEvent } = getOfflineState(device);

	const lastSeen = `${t("device.state.lastSeen")}: ${getUiDateTime(presence.lastSeenAtSeconds ?? device.updatedAt)}`;
	const offline = heuristicOnly ? presence.isOffline : isOffline;

	if (offline) {
		const tooltip = heuristicOnly
			? `${t("device.state.presenceEstimated")}. ${lastSeen}`
			: [offlineSince && `${t("device.state.offlineSince")}: ${getUiDateTime(offlineSince)}`, lastSeen]
					.filter(Boolean)
					.join(". ");

		return (
			<PillItem
				type="full"
				value={t("device.state.presence.offline")}
				icon="pi-wifi offlineIcon"
				wrapperStyle="grey"
				tooltip={tooltip}
			/>
		);
	}

	if (showWarning && isSilentWithoutEvent) {
		//Still online, because the event says so. The icon is the only difference, since there is
		//no amber pill style in this codebase - wrapperStyle has success, error, grey and blue1
		//only - and fontColour-warn on the icon gets the warning across without a new style and a
		//sass rebuild.
		return (
			<PillItem
				type="full"
				value={t("device.state.presence.online")}
				icon="pi-exclamation-triangle fontColour-warn"
				wrapperStyle="success"
				tooltip={`${t("device.state.silentWithoutEvent")}. ${lastSeen}`}
			/>
		);
	}

	return <PillItem type="text" value={t("device.state.presence.online")} wrapperStyle="success" />;
};

//Alarm (red) / Clear (green) pill, live from the alarm atom
export const AlarmStatePill = ({ device }) => {
	const { t } = useTranslation();
	const activeAlarms = useRecoilValue(deviceSensorAlarmListAtom);
	const inAlarm = activeAlarms?.some((alarm) => alarm?.device?.id === device.id) ?? false;

	return inAlarm ? (
		<PillItem type="text" value={t("alarm.group.details.state.alarm")} wrapperStyle="error" />
	) : (
		<PillItem type="text" value={t("alarm.group.details.state.clear")} wrapperStyle="success" />
	);
};
